<template>
  <!-- 발행 추이 위젯 (M5) — 단위(일/월/년) · 비교(없음/직전 기간/전년 동기) · 형태(막대/꺾은선) -->
  <div class="column-box trend-widget">
    <div class="trend-widget-header">
      <div class="trend-header-top">
        <h3>{{ $t('dashboard_trend_title') }}</h3>
        <span v-if="!loading && !error" class="trend-widget-note">
          {{ $t('dashboard_trend_header_note', { count: formatCommaNumber(totalCount) }) }}
        </span>
      </div>
      <div class="trend-controls">
        <div class="trend-control-group">
          <span class="trend-control-label">{{ $t('dashboard_trend_control_granularity_label') }}</span>
          <a-radio-group :value="granularity" size="small" @change="onGranularityChange">
            <a-tooltip :title="dailyDisabled ? $t('dashboard_trend_daily_disabled_tooltip') : ''">
              <a-radio-button value="day" :disabled="dailyDisabled">{{ $t('dashboard_trend_granularity_day') }}</a-radio-button>
            </a-tooltip>
            <a-radio-button value="month">{{ $t('dashboard_trend_granularity_month') }}</a-radio-button>
            <a-radio-button value="year">{{ $t('dashboard_trend_granularity_year') }}</a-radio-button>
          </a-radio-group>
        </div>
        <a-tooltip :title="compareDisabled ? $t('dashboard_trend_compare_disabled_tooltip') : ''">
          <div class="trend-control-group">
            <span class="trend-control-label">{{ $t('dashboard_trend_control_compare_label') }}</span>
            <a-radio-group :value="compare" size="small" :disabled="compareDisabled" @change="onCompareChange">
              <a-radio-button value="none">{{ $t('dashboard_trend_compare_none') }}</a-radio-button>
              <a-radio-button value="previous">{{ $t('dashboard_trend_compare_previous') }}</a-radio-button>
              <a-radio-button value="yoy">{{ $t('dashboard_trend_compare_yoy') }}</a-radio-button>
            </a-radio-group>
          </div>
        </a-tooltip>
        <div class="trend-control-group">
          <span class="trend-control-label">{{ $t('dashboard_trend_control_chart_type_label') }}</span>
          <a-radio-group v-model:value="localChartType" size="small">
            <a-radio-button value="bar">{{ $t('dashboard_trend_chart_type_bar') }}</a-radio-button>
            <a-radio-button value="line">{{ $t('dashboard_trend_chart_type_line') }}</a-radio-button>
          </a-radio-group>
        </div>
      </div>
    </div>
    <DashboardWidgetState
      :loading="loading"
      :error="error"
      :is-empty="isEmpty"
      :empty-message="emptyMessage"
      @retry="$emit('retry')"
    >
      <div class="trend-chart-wrap">
        <canvas ref="canvasRef" />
      </div>
    </DashboardWidgetState>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dayjs from 'dayjs';
import DashboardWidgetState from './DashboardWidgetState.vue';
import { formatCommaNumber } from '@/utils/utils';
import { MAX_DAILY_BUCKETS, resolveTrendGuards } from '@/utils/dashboard-prefs';

// 와이어프레임 톤: 선택 기간(진한 남색) / 비교 기간(옅은 회색, 대시 라인으로 이중 부호화)
const CURRENT_COLOR = '#3D4A5C';
const COMPARE_COLOR = '#C9CED6';
// 비교 기간 라벨 텍스트 — 막대색(#C9CED6)은 흰 배경에서 대비가 부족해 한 단계 짙은 회색을 쓴다.
const COMPARE_LABEL_COLOR = '#8D939A';
// 데이터라벨을 그릴 최대 버킷 수 — 이보다 조밀하면(일별 최대 400) 라벨끼리 겹쳐 오히려 난독이라 숨긴다.
const DATALABEL_MAX_BUCKETS = 31;

export default {
  name: 'TrendWidget',
  components: { DashboardWidgetState },
  emits: ['retry', 'params-change', 'chart-type-change'],
  props: {
    buckets: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    compareTotalCount: { type: Number, default: null },
    // 단위: day / month(기본) / year
    granularity: { type: String, default: 'month' },
    // 비교: none / previous / yoy(기본)
    compare: { type: String, default: 'yoy' },
    // 형태(막대/꺾은선) — localStorage 하이드레이션을 위해 부모가 초기값을 내려주고,
    // 이후 변경은 로컬에서 즉시 반영 + chart-type-change로 부모(영속화 담당)에 통지한다.
    chartType: { type: String, default: 'bar' },
    // 선택 기간이 "전체"인지, 며칠인지 — 클라이언트 가드(400 상한, 전체 기간 비교 불가) 판단용
    isAllPeriod: { type: Boolean, default: false },
    periodDayCount: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    isEmpty: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
  },
  data() {
    return {
      formatCommaNumber,
      localChartType: this.chartType,
      // 비교가 가드에 의해 강제로 "없음"으로 바뀌기 전의 선택값(가드 해제 시 복원용)
      previousCompare: null,
      // scheduleRender() 중복 호출 방지 플래그. 같은 nextTick 틱 안에서 여러 watcher가
      // 동시에 트리거되어도(buckets+loading 등) renderChart()는 1회만 실행한다.
      renderPending: false,
    };
  },
  created() {
    // 근본 수정: chart.js 인스턴스를 data()에 두면 Vue 3가 깊은 반응형 프록시로 감싸고,
    // chart.js 옵션 리졸버가 그 프록시를 순회하다 무한 재귀(Maximum call stack size
    // exceeded)에 빠진다. data() 밖에서 대입되는 인스턴스 속성은 반응형이 아니므로
    // 여기서 초기화한다(생성 시점에는 markRaw로 한 번 더 방어한다).
    this.chartInstance = null;
  },
  computed: {
    // 기간이 "전체"이거나 400 버킷을 넘어설 만큼 길면 일별 보기를 막는다(백엔드 400 방지).
    dailyDisabled() {
      return this.isAllPeriod || this.periodDayCount > MAX_DAILY_BUCKETS;
    },
    // "전체" 기간이거나 단위가 "년별"이면 비교 자체를 막는다(백엔드 규칙: 기간 필요, 년별은 비교 불가).
    compareDisabled() {
      return this.isAllPeriod || this.granularity === 'year';
    },
  },
  watch: {
    // QA 버그 수정: 모든 재렌더 트리거를 scheduleRender()로 통일한다. DashboardWidgetState가
    // loading/isEmpty 상태에 따라 canvas를 마운트/언마운트하므로, DOM 패치 전에 렌더하면
    // this.$refs.canvasRef가 최신 상태가 아니어서 빈 화면으로 남을 수 있다.
    // (회귀 수정) 같은 fetch 완료 시점에 buckets/loading이 동시에 바뀌면 각자
    // $nextTick(renderChart)를 걸어 같은 틱에 renderChart()가 여러 번 실행되고, 두 번째
    // 호출부터 "warm"(destroy 후 재생성) 경로를 타 빈 캔버스로 남았다 — scheduleRender()가
    // 같은 틱 안의 중복 호출을 1회로 합친다.
    buckets() {
      this.scheduleRender();
    },
    loading(newVal) {
      if (!newVal) this.scheduleRender();
    },
    localChartType(newVal) {
      this.scheduleRender();
      if (newVal !== this.chartType) this.$emit('chart-type-change', newVal);
    },
    // 기간 변경으로 가드 조건이 바뀌면(전체 진입/이탈, 기간 길이 변화) 현재 선택을 조용히 보정한다.
    // dashboard-prefs.js의 resolveTrendGuards를 그대로 재사용해 로드 시 하이드레이션과
    // 동일한 규칙으로 판단한다(로직 두 곳 분기 방지).
    dailyDisabled(newVal) {
      if (newVal && this.granularity === 'day') {
        const guarded = resolveTrendGuards({
          granularity: this.granularity,
          compare: this.compare,
          isAllPeriod: this.isAllPeriod,
          dayCount: this.periodDayCount,
        });
        this.$emit('params-change', guarded);
      }
    },
    compareDisabled(newVal) {
      if (newVal && this.compare !== 'none') {
        this.previousCompare = this.compare;
        this.$emit('params-change', { granularity: this.granularity, compare: 'none' });
      } else if (!newVal && this.compare === 'none' && this.previousCompare) {
        const restored = this.previousCompare;
        this.previousCompare = null;
        this.$emit('params-change', { granularity: this.granularity, compare: restored });
      }
    },
  },
  mounted() {
    this.renderChart();
  },
  beforeUnmount() {
    if (this.chartInstance) this.chartInstance.destroy();
  },
  methods: {
    // (회귀 수정) 같은 nextTick 틱 안에서 여러 watcher가 동시에 트리거되어도
    // renderChart()는 1회만 실행되도록 예약을 합친다.
    scheduleRender() {
      if (this.renderPending) return;
      this.renderPending = true;
      this.$nextTick(() => {
        this.renderPending = false;
        this.renderChart();
      });
    },
    onGranularityChange(e) {
      const value = e.target.value;
      // 년별 전환 시 비교가 켜져 있으면 즉시 끈다(가드 왕복 이중 emit 방지).
      let nextCompare = this.compare;
      if (value === 'year' && this.compare !== 'none') {
        this.previousCompare = this.compare;
        nextCompare = 'none';
      }
      this.$emit('params-change', { granularity: value, compare: nextCompare });
    },
    onCompareChange(e) {
      if (this.compareDisabled) return;
      this.previousCompare = null;
      this.$emit('params-change', { granularity: this.granularity, compare: e.target.value });
    },
    compareLabel() {
      return this.compare === 'previous'
        ? this.$i18n.t('dashboard_trend_compare_previous')
        : this.$i18n.t('dashboard_trend_compare_yoy');
    },
    // 단위별 날짜 포맷: 일별 "YYYY년 M월 D일" / 월별 "YYYY년 M월" / 년별 "YYYY년"
    formatBucketDate(dateStr) {
      if (!dateStr) return '';
      const d = dayjs(dateStr);
      const yearUnit = this.$i18n.t('dashboard_trend_unit_year');
      const monthUnit = this.$i18n.t('dashboard_trend_unit_month');
      const dayUnit = this.$i18n.t('dashboard_trend_unit_day');
      if (this.granularity === 'year') return `${d.format('YYYY')}${yearUnit}`;
      if (this.granularity === 'month') return `${d.format('YYYY')}${yearUnit} ${d.format('M')}${monthUnit}`;
      return `${d.format('YYYY')}${yearUnit} ${d.format('M')}${monthUnit} ${d.format('D')}${dayUnit}`;
    },
    // x축 라벨: 일별 "M/D" / 월별 "M월"(1월 또는 첫 tick엔 연도 포함) / 년별 "YYYY"
    xAxisLabel(dateStr, index) {
      const d = dayjs(dateStr);
      const monthUnit = this.$i18n.t('dashboard_trend_unit_month');
      const yearUnit = this.$i18n.t('dashboard_trend_unit_year');
      if (this.granularity === 'year') return d.format('YYYY');
      if (this.granularity === 'month') {
        return d.month() === 0 || index === 0 ? `${d.format('YYYY')}${yearUnit} ${d.format('M')}${monthUnit}` : `${d.format('M')}${monthUnit}`;
      }
      return d.format('M/D');
    },
    renderChart() {
      if (!this.$refs.canvasRef || this.loading || this.error || this.isEmpty) return;
      // QA 버그 수정: 렌더 도중 예외가 발생해도 크래시하거나 빈 캔버스로 방치되지 않도록
      // destroy + new Chart 전체를 try/catch로 감싼다.
      try {
      const labels = this.buckets.map((b, index) => this.xAxisLabel(b.currentStart, index));
      const currentData = this.buckets.map((b) => b.current);
      // compare가 null인 버킷은 그대로 null 유지 — chart.js가 막대/포인트를 그리지 않음(0이 아님).
      const hasCompare = this.compare !== 'none';
      const compareData = this.buckets.map((b) => (b.compare === undefined ? null : b.compare));

      const datasets = [
        {
          datasetKey: 'current',
          label: this.$i18n.t('dashboard_trend_legend_current'),
          type: this.localChartType,
          data: currentData,
          backgroundColor: CURRENT_COLOR,
          borderColor: CURRENT_COLOR,
          maxBarThickness: 28,
          tension: 0.25,
          pointRadius: this.localChartType === 'line' ? 3 : 0,
          pointBackgroundColor: CURRENT_COLOR,
        },
      ];

      if (hasCompare) {
        datasets.push({
          datasetKey: 'compare',
          label: this.compareLabel(),
          type: this.localChartType,
          data: compareData,
          backgroundColor: COMPARE_COLOR,
          borderColor: COMPARE_COLOR,
          borderDash: this.localChartType === 'line' ? [6, 4] : undefined,
          maxBarThickness: 28,
          tension: 0.25,
          pointRadius: this.localChartType === 'line' ? 2 : 0,
          pointBackgroundColor: COMPARE_COLOR,
        });
      }

      const buckets = this.buckets;
      const formatBucketDate = this.formatBucketDate;
      const compareLabel = this.compareLabel();

      const chartOptions = {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: 'index' },
          // 막대 위(anchor: end)에 라벨을 올리므로 최상단 값이 차트 밖으로 잘리지 않게 여백 확보.
          layout: { padding: { top: 16 } },
          plugins: {
            legend: { display: true, position: 'top', align: 'end' },
            // 라벨은 막대 "안"이 아니라 막대 "위"(흰 배경)에 얹어 막대색과 무관하게 항상 읽히게 한다.
            // 시리즈 색과 톤을 맞춰(남색/회색) 어떤 숫자가 어느 막대 것인지 시각적으로 연결한다.
            datalabels: {
              display: (context) => {
                if (context.dataset.data.length > DATALABEL_MAX_BUCKETS) return false;
                const value = context.dataset.data[context.dataIndex];
                // null 비교 버킷은 숨기고, 'auto'로 라벨끼리 충돌 시 자동 생략한다.
                return value === null || value === undefined ? false : 'auto';
              },
              anchor: 'end',
              align: 'end',
              offset: 2,
              clamp: true,
              clip: false,
              color: (context) =>
                context.dataset.datasetKey === 'compare' ? COMPARE_LABEL_COLOR : CURRENT_COLOR,
              font: { size: 11, weight: 600 },
              formatter: (value) => formatCommaNumber(value),
            },
            tooltip: {
              // compare 값이 없는(null) 버킷은 툴팁에서 아예 숨긴다(0으로 오인 방지).
              filter: (item) => item.raw !== null && item.raw !== undefined,
              callbacks: {
                title: () => '',
                label: (context) => {
                  const bucket = buckets[context.dataIndex];
                  if (!bucket) return '';
                  const count = `${formatCommaNumber(context.raw)}건`;
                  if (context.dataset.datasetKey === 'compare') {
                    return `${compareLabel}(${formatBucketDate(bucket.compareStart)}): ${count}`;
                  }
                  return `${formatBucketDate(bucket.currentStart)}: ${count}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: '#6e747c',
                font: { size: 12 },
                autoSkip: true,
                maxRotation: 0,
                minRotation: 0,
              },
            },
            y: {
              beginAtZero: true,
              grid: { color: '#f1f2f3', drawBorder: false },
              ticks: { color: '#6e747c', precision: 0 },
            },
          },
      };

      // (회귀 수정) "warm" 재렌더(막대/꺾은선 전환, 비교 on/off 등)는 destroy 후 재생성
      // 대신 기존 인스턴스를 제자리 갱신한다. 데이터셋 개수·타입이 바뀌어도 data.labels·
      // data.datasets·options를 통째로 교체 후 update()하면 chart.js v4가 올바르게 반영한다.
      const sameCanvas = this.chartInstance && this.chartInstance.canvas === this.$refs.canvasRef;
      if (sameCanvas) {
        this.chartInstance.data.labels = labels;
        this.chartInstance.data.datasets = datasets;
        this.chartInstance.options = chartOptions;
        this.chartInstance.update();
      } else {
        if (this.chartInstance) {
          this.chartInstance.destroy();
          this.chartInstance = null;
        }
        // (근본 수정) this.chartInstance 참조가 유실된 경우에도(예: 이전 렌더에서 예외로
        // this.chartInstance만 null로 정리된 경우) 해당 canvas에 등록된 chart.js 인스턴스가
        // 남아있으면 "Canvas is already in use" 예외가 난다 — 레지스트리에서 직접 조회해 제거한다.
        const existing = Chart.getChart(this.$refs.canvasRef);
        if (existing) existing.destroy();
        const ctx = this.$refs.canvasRef.getContext('2d');
        // markRaw: data()에 두지 않아 이미 비반응형이지만, 향후 리팩터로 실수 유입되는 것까지
        // 방어하기 위해 이중으로 markRaw 처리한다(Vue 3 + chart.js 무한 재귀 회귀 방지).
        this.chartInstance = markRaw(new Chart(ctx, {
          type: this.localChartType,
          data: { labels, datasets },
          // datalabels는 로컬 플러그인으로 명시 등록한다. 다른 화면(GoalManagement)이 전역 등록한
          // 기본 설정(짙은 회색 라벨)이 이 차트에 흘러들어 남색 막대와 겹쳐 안 보이는 문제의 원인이었다.
          plugins: [ChartDataLabels],
          options: chartOptions,
        }));
      }
      } catch (error) {
        // 렌더 실패 시 크래시 없이 로그만 남기고 인스턴스를 정리한다(빈 캔버스 방치 방지).
        console.error('[dashboard] renderChart failed', error);
        // (근본 수정) chartInstance를 그냥 null로 버리면 canvas에는 여전히 살아있는 chart.js
        // 인스턴스가 등록되어 있어 이벤트 리스너가 orphan으로 남고("Cannot read properties of
        // undefined" 스팸), 다음 cold create가 "Canvas is already in use"로 실패한다.
        try {
          const orphan = Chart.getChart(this.$refs.canvasRef);
          if (orphan) orphan.destroy();
        } catch (cleanupError) {
          // 정리 실패는 무시한다(원본 오류가 이미 로그됨).
        }
        this.chartInstance = null;
      }
    },
  },
};
</script>

<style scoped>
.trend-widget {
  width: 100%;
  box-sizing: border-box;
}

.trend-widget-header {
  margin-bottom: 14px;
}

.trend-header-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.trend-header-top h3 {
  margin-bottom: 0;
}

.trend-widget-note {
  font-size: 13px;
  color: #8d939a;
  white-space: nowrap;
}

.trend-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 20px;
  row-gap: 8px;
  margin-top: 12px;
}

.trend-control-group {
  display: flex;
  align-items: center;
  column-gap: 8px;
}

.trend-control-label {
  font-size: 13px;
  color: #8d939a;
  white-space: nowrap;
}

/* Chart.js 리사이즈 진동 방지 표준 패턴(DemographicsChart와 동일):
   부모 position:relative + 고정 height, 캔버스는 absolute로 꽉 채운다. */
.trend-chart-wrap {
  position: relative;
  width: 100%;
  height: 320px;
}

.trend-chart-wrap canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
}
</style>
